import { createSelector } from '@reduxjs/toolkit';
import { VIEW_VARIANTS } from 'components/ViewVariant/types';
import { get } from 'lodash';
import { snakeToCamelCaseKeys } from 'services/helpers';
import FileService from 'services/FileService';
import {
  getBillingPermissionFlagsFromSection,
  getBillingPermissionsFlagsFromBusiness,
} from 'ts-components/hooks/permissions/billing';
import { UI_THEMES } from '@kizen/kds/Colors';

const aliases = {
  contacts: {
    create: 'can_create_new_contact',
    action: 'bulk_actions',
  },
};

export const bulkActionsAliases = {
  sendEmail: 'bulk_send_email',
  sendText: 'bulk_send_text',
  sendSurvey: 'bulk_send_survey',
  changeTags: 'bulk_change_tags',
  changeFieldValue: 'bulk_change_field_value',
  exportToCsv: 'bulk_export_to_csv',
  archive: 'bulk_archive',
  startAutomation: 'bulk_start_automation',
  manageAssociations: 'bulk_manage_associations',
  uploadContacts: 'bulk_data_upload',
};

/*
 * Allows us to ask rules about user access/permissions
 * in a slightly simpler way than working with the API's raw result.
 *
 * Example usage:
 *
 * can(state, {
 *   view: 'section',
 *   for: 'contacts'
 * });
 *
 * can(state, {
 *   create: 'contacts'
 * });
 */

export const can = ({ authentication }, { for: ruleFor, ...rule }) => {
  const { access } = authentication;
  if (!access) {
    return false;
  }
  const ruleEntries = Object.entries(rule);
  if (ruleEntries.length !== 1) {
    return false;
  }

  const [[permissionNameRaw, area]] = ruleEntries;
  if (!access[area]) {
    return false;
  }
  const permissionName =
    (aliases[area] && aliases[area][permissionNameRaw]) || permissionNameRaw;
  const actionName = bulkActionsAliases[ruleFor];
  const allowed = ruleFor
    ? actionName
      ? access[area][permissionName][actionName]
      : get(access[area], `${ruleFor}.${permissionName}`)
    : access[area][permissionName];
  // Checking for true protects from missing "for" argument,
  // when "allowed" may be an object containing the relevant permissions.
  return allowed === true;
};

export const selectCanEditContactObjects = ({ authentication }) => {
  return (
    authentication.access?.sections?.custom_objects_section?.contacts?.edit ??
    false
  );
};

export const selectCanEditTeamMemebersFields = ({ authentication }) => {
  return (
    authentication.access?.sections?.settings_section
      ?.team_member_field_settings?.edit ?? false
  );
};

export const selectCanDeleteTeamMemebersFields = ({ authentication }) => {
  return (
    authentication.access?.sections?.settings_section
      ?.team_member_field_settings?.remove ?? false
  );
};

const selectCanEditAllCustomObjects = ({ authentication }) => {
  return (
    authentication.access?.sections?.custom_objects_section?.all_records
      ?.edit ?? false
  );
};

const selectCanEditAssociatedCustomObjects = ({ authentication }) => {
  return (
    authentication.access?.sections?.custom_objects_section?.associated_records
      ?.edit ?? false
  );
};

export const selectCanEditSomeCustomObjects = createSelector(
  selectCanEditAllCustomObjects,
  selectCanEditAssociatedCustomObjects,
  (all, associated) => all || associated
);

export const getSettingsAccess = createSelector(
  (state) => state.authentication.access,
  selectCanEditContactObjects,
  selectCanEditSomeCustomObjects,
  (state) => state.authentication?.chosenBusiness,
  (access, canEditContacts, canEditObjects, chosenBusiness) => {
    const {
      ai_business_generation,
      business_customization,
      business_information,
      business_holidays,
      team_member,
      permission_group,
      role,
      team_member_field_settings,
      domain_and_tracking,
      subscription_list,
      privacy,
      integrated_inbox,
      manage_standard_api_keys,
      manage_oauth_authorizations,
      manage_integration_secrets,
      manage_service_accounts,
      meta_export_import,
      text_messaging,
    } = access.sections.settings_section || {};

    const { canViewBillingSettingsMenuItem, canViewUsage } =
      getBillingPermissionFlagsFromSection(access.sections.settings_section);

    const { canSelfServeBilling } =
      getBillingPermissionsFlagsFromBusiness(chosenBusiness);

    const { custom_objects_section } = access.sections;
    const { customize_field } = custom_objects_section;
    return {
      aiBusinessGeneration: {
        enabled: ai_business_generation.edit,
      },
      business: {
        enabled:
          business_customization?.edit ||
          business_information?.edit ||
          business_holidays?.view ||
          meta_export_import?.edit,
        businessCustomization: business_customization,
        businessInformation: business_information,
        businessHolidays: business_holidays,
        metaExportImport: meta_export_import,
      },
      team: {
        enabled: team_member?.view || permission_group?.view || role?.view,
        teamMembers: team_member,
        permissionGroups: permission_group,
        roles: role,
      },
      customFields: {
        enabled:
          (customize_field?.edit && (canEditContacts || canEditObjects)) ||
          team_member_field_settings?.edit,
        customizeFields: customize_field,
      },
      domainAndTracking: {
        enabled: domain_and_tracking?.view,
        domainAndTracking: domain_and_tracking,
      },
      privacySettings: {
        enabled:
          subscription_list?.edit || privacy?.edit || integrated_inbox?.edit,
        subscriptionLists: subscription_list,
        privacy,
        integratedInboxes: integrated_inbox,
        textMessaging: text_messaging,
      },
      billing: {
        enabled: canSelfServeBilling
          ? canViewBillingSettingsMenuItem
          : canViewUsage,
        hideFromNav: false,
      },
      manageConnections: {
        enabled:
          manage_standard_api_keys?.view ||
          manage_oauth_authorizations?.view ||
          manage_integration_secrets?.view ||
          manage_service_accounts?.view,
        manageStandardApiKeys: manage_standard_api_keys,
        manageOauthAuthorizations: manage_oauth_authorizations,
        manageIntegrationSecrets: manage_integration_secrets,
        manageServiceAccounts: manage_service_accounts,
      },
    };
  }
);

export const getCustomObjectSettinsAccess = ({
  authentication: { access },
}) => {
  return access.sections.custom_objects_section;
};

export const getSettingsRoutesWithAccess = createSelector(
  getSettingsAccess,
  (_, routes) => routes,
  (settingsAccess, routes) => {
    const settingsRoutesWithAccess = Object.entries(routes).reduce(
      (collect, [key, value]) => ({
        ...collect,
        [key]: { ...value, access: settingsAccess[key] },
      }),
      {}
    );

    const accessibleSettingsRoutes = Object.entries(
      settingsRoutesWithAccess
    ).reduce(
      (collect, [key, value]) =>
        settingsAccess[key]?.enabled
          ? {
              ...collect,
              [key]: {
                ...value,
                access: settingsAccess[key],
              },
            }
          : collect,
      {}
    );

    const firstAccesssibleSettingsRoute = Object.values(
      accessibleSettingsRoutes
    )[0];
    return {
      settingsRoutesWithAccess,
      accessibleSettingsRoutes,
      firstAccesssibleSettingsRoute,
    };
  }
);

export const getBroadcastsAccess = createSelector(
  ({ authentication }) => authentication.access.sections.broadcasts_section,
  ({ view, enabled, ...access }) => {
    return {
      canView: view && enabled,
      canScheduleBroadcasts: Object.values(access).some(
        ({ edit, remove }) => edit || remove
      ),
      access,
    };
  }
);

export const getCustomObjectsWithBulkAccess = createSelector(
  ({ authentication }) => ({
    ...authentication.access.custom_objects.custom_object_entities,
    [authentication.chosenBusiness?.client_object.id]:
      authentication.access.contacts.bulk_actions,
  }),
  (_, bulkKey) => bulkKey,
  (objects, bulkKey) =>
    Object.entries(objects).reduce(
      (collect, [id, { [bulkKey]: bulkAccessValue }]) => ({
        ...collect,
        [id]: bulkAccessValue,
      }),
      {}
    )
);

const selectSheduledActivities = ({ authentication }) =>
  authentication.access.scheduled_activities || {};

const selectCustomObjectsEntities = ({ authentication }, clientObjectId) =>
  authentication.access.custom_objects.custom_object_entities?.[
    clientObjectId
  ] || {};

const selectContact = ({ authentication }) =>
  authentication.access.contacts || {};

const selectContactsAndScheduledActivities = (state) => ({
  entities: selectContact(state),
  scheduleActivitiesAccess: selectSheduledActivities(state),
});
const selectCustomObjectAndScheduledActivities = (state, clientObjectId) => ({
  entities: selectCustomObjectsEntities(state, clientObjectId),
  scheduleActivitiesAccess: selectSheduledActivities(state),
});

// make it more user friendly
const recordLayoutMapper = () => {
  console.log(
    'This permission has been deprecated and will be fully removed in a future sprint'
  );
  return {};
};

export const getCustomObjectsRecordLayoutAccess = createSelector(
  selectCustomObjectAndScheduledActivities,
  recordLayoutMapper
);

export const getContactRecordLayoutAccess = createSelector(
  selectContactsAndScheduledActivities,
  recordLayoutMapper
);

export const showDataDropdown = ({ authentication }, preReleaseFeatures) => {
  const { access } = authentication;
  if (!access) {
    return false;
  }

  const sections = snakeToCamelCaseKeys(access.sections);
  // Show main nav item if any of it's children has permission set to view: true
  return !!(
    sections.contactsSection.view ||
    // (preReleaseFeatures && sections.companiesSection.view) || TODO need to add when companies would be in the app
    // sections.pipelinesSection.view || TODO need to add when pipelines would be in the app
    sections.customObjectsSection.view ||
    (preReleaseFeatures && sections.ordersSection.view) ||
    (preReleaseFeatures && sections.productsSection.view) ||
    sections.marketingCostsSection.view
  );
};

export const showPlatformDropdown = (
  { authentication },
  preReleaseFeatures
) => {
  const { access } = authentication;

  if (!access) {
    return false;
  }

  const sections = snakeToCamelCaseKeys(access.sections);
  // Show main nav item if any of it's children has permission set to view: true
  return !!(
    (preReleaseFeatures && sections.broadcastsSection.view) ||
    sections.formsSection.view ||
    sections.adsSection.view ||
    sections.librarySection.view ||
    sections.surveysSection.view
  );
};

export const getChosenBusiness = ({ authentication }) => {
  return authentication?.chosenBusiness || null;
};

export const getChosenBusinessEntitlement =
  /** @type {(state: any, entitlementKey: string) => any} */ (
    createSelector(
      getChosenBusiness,
      (_, entitlementKey) => entitlementKey,
      (chosenBusiness, entitlementKey) => {
        return chosenBusiness?.entitlements?.[entitlementKey] || null;
      }
    )
  );

export const getUIThemes = createSelector(
  (state) => getChosenBusinessEntitlement(state, 'ui_themes'),
  (uiThemes) => {
    const options = [UI_THEMES.LIGHT, UI_THEMES.HIGH_CONTRAST];
    if (!uiThemes || !Array.isArray(uiThemes)) {
      return options;
    }
    uiThemes.forEach((theme) => {
      switch (theme) {
        case UI_THEMES.QA:
          options.push(UI_THEMES.QA);
          break;
        case UI_THEMES.BRAND:
          options.push(UI_THEMES.BRAND);
          break;
        default:
          break;
      }
    });
    return options;
  }
);

export const getCurrentTeamMemberTheme = (state) =>
  state.authentication.teamMember?.theme;

export const getCurrentTheme = createSelector(
  getCurrentTeamMemberTheme,
  getUIThemes,
  (theme, uiThemes) => {
    if (!theme || !uiThemes.includes(theme)) {
      return UI_THEMES.LIGHT;
    }
    return theme;
  }
);

export const getBusinessClientObject = createSelector(
  getChosenBusiness,
  (chosenBusiness) => {
    if (!chosenBusiness) {
      return {
        id: null,
        objectName: null,
        access: false,
      };
    }
    return {
      id: chosenBusiness.client_object.id,
      objectName: chosenBusiness.client_object.object_name,
      access: chosenBusiness.client_object.access,
    };
  }
);

export const getBusinessTeamMembersObject = createSelector(
  getChosenBusiness,
  (chosenBusiness) => {
    if (!chosenBusiness) {
      return {
        id: null,
        name: null,
        objectName: null,
        categories: [],
      };
    }
    return {
      id: chosenBusiness.employee_object.id,
      name: chosenBusiness.employee_object.name,
      objectName: chosenBusiness.employee_object.object_name,
      categories: [chosenBusiness.employee_object.default_category],
    };
  }
);

export const getCustomObjectAccess = ({ authentication }) => {
  const { access } = authentication;
  return snakeToCamelCaseKeys(access.custom_objects);
};

export const getCustomObjectEntitiesAccess = ({ authentication }) => {
  const { access } = authentication;
  return access.custom_objects.custom_object_entities;
};

export const getCustomObjectEntitiesAccessById = ({ authentication }, id) => {
  const { access } = authentication;
  return snakeToCamelCaseKeys(
    access.custom_objects.custom_object_entities?.[id]
  );
};

export const getContactSectionAccess = ({ authentication }) => {
  const { access } = authentication;
  return snakeToCamelCaseKeys(access.sections.contacts_section);
};

export const getCustomObjectsSettingsAccess = ({ authentication }) => {
  const { access } = authentication;
  return snakeToCamelCaseKeys(access.custom_objects.object_settings);
};

export const getContactsAccess = ({ authentication }) => {
  const { access } = authentication;
  return access.contacts;
};

export const getCreateEntityAccess = createSelector(
  getCustomObjectEntitiesAccess,
  getContactsAccess,
  getBusinessClientObject,
  (customObjectEntitiesAccess, contactsAccess, { id }) => {
    return {
      [id]: contactsAccess.can_create_new_contact,
      ...Object.entries(customObjectEntitiesAccess).reduce(
        (acc, [id, access]) => ({ ...acc, [id]: access.can_create }),
        {}
      ),
    };
  }
);

export const getEntityViewAccess = createSelector(
  getCustomObjectEntitiesAccess,
  ({ authentication }) => authentication.access.sections.contacts_section,
  getBusinessClientObject,
  (customObjectEntitiesAccess, contactsAccess, { id }) => {
    return {
      [id]: contactsAccess.view,
      ...Object.entries(customObjectEntitiesAccess).reduce(
        (acc, [id, access]) => ({ ...acc, [id]: access.enabled }),
        {}
      ),
    };
  }
);

const buildRecordOverviewAccess = ({
  record_overview_board_view,
  record_overview_chart_view,
  record_overview_quick_filters,
}) => {
  return {
    viewVariantsAccess: {
      [VIEW_VARIANTS.TABLE]: true,
      [VIEW_VARIANTS.BOARD]: !!record_overview_board_view,
      [VIEW_VARIANTS.CHARTS]: !!record_overview_chart_view?.view,
    },
    quickFiltersAccess: !!record_overview_quick_filters,
  };
};

export const getRecordOverviewAccess = createSelector(
  ({ authentication }, objectId) => {
    if (objectId) {
      return (
        authentication?.access?.custom_objects?.custom_object_entities?.[
          objectId
        ]?.record_overview ?? {}
      );
    }
    return authentication?.access?.contacts?.record_overview ?? {};
  },
  buildRecordOverviewAccess
);

const noViewAccess = {
  view: false,
  edit: false,
  delete: false,
};

export const getRecordOverviewChartViewAccess =
  (objectId) =>
  ({ authentication }) => {
    if (objectId) {
      return (
        authentication?.access?.custom_objects?.custom_object_entities?.[
          objectId
        ]?.record_overview?.record_overview_chart_view || noViewAccess
      );
    }
    return (
      authentication?.access?.contacts?.record_overview
        ?.record_overview_chart_view || noViewAccess
    );
  };

export const getDashboardsAccess = ({ authentication }) => {
  const { access } = authentication;
  return access.sections.dashboards_section.customize_dashboards;
};

export const getAuthTeamMember = createSelector(
  ({ authentication }) => authentication.teamMember,
  snakeToCamelCaseKeys
);

export const getUserEmail = (s) => s.authentication?.teamMember?.email ?? '';

export const getUserProfile = (state) => {
  const { authentication } = state;

  return authentication && authentication.teamMember;
};

export const getApiKeysAccess = ({ authentication: { access } }) => {
  return access.sections.api_connections_section.standard_api_keys;
};

export const getOAuthTokenAccess = ({ authentication: { access } }) => {
  return access.sections.api_connections_section.oauth_authorizations;
};

export const getKeyAndTokenAccess = createSelector(
  getApiKeysAccess,
  getOAuthTokenAccess,
  (accessApi, accessOauth) => {
    return accessApi || accessOauth;
  }
);

export const getMyProfileSection = ({ authentication: { access } }) => {
  return access.sections.my_profile_section.view;
};

export const getManageOauthAuthorizations = ({
  authentication: { access },
}) => {
  return access.sections.settings_section.manage_oauth_authorizations;
};

export const getManageStanadardApiKeys = ({ authentication: { access } }) => {
  return access.sections.settings_section.manage_standard_api_keys;
};

export const getManageIntegrationSecrets = ({ authentication: { access } }) => {
  return access.sections.settings_section.manage_integration_secrets;
};

export const getManageServiceAccounts = ({ authentication: { access } }) => {
  return access.sections.settings_section.manage_service_accounts;
};

export const getBusinessTimeZone = (state) =>
  state.authentication.chosenBusiness.timezone.name;

export const canSetAdvancedRecurrenceRule = (state) => {
  return getChosenBusinessEntitlement(state, 'show_advanced_rrule');
};

export const isKizenKnowledgeBaseEnabled = (state) => {
  return (
    getChosenBusinessEntitlement(state, 'ai_kizen_knowledgebase_enabled') ||
    false
  );
};

export const getSmartConnectorAccess = ({ authentication: { access } }) => {
  return access.smart_connectors;
};

export const getTeamMemberNameAndImage = createSelector(
  ({ authentication }) => authentication.teamMember,
  (teamMember) => ({
    userName: teamMember?.full_name,
    userImage: teamMember?.picture
      ? FileService.getThumbnailUrl(teamMember.picture.id)
      : null,
  })
);
